import * as React from 'react';
import { useToggle } from '@deltasierra/react-hooks';
import { ClickAwayListener, Grow } from '@material-ui/core';
import { DSPopper } from '../Popper';
import { DSPaper } from '../Paper';

export type DSDropdownProps = {
    render: (toggle: React.Dispatch<'toggle' | 'toggleOff' | 'toggleOn'>) => JSX.Element;
    align?: 'center' | 'left' | 'right';
    children: (requestClose: () => void) => JSX.Element;
    dropUp?: boolean;
    renderPaper?: (children: React.ReactNode) => JSX.Element;
};

type DropdownPlacement = 'bottom-end' | 'bottom-start' | 'bottom' | 'top-end' | 'top-start' | 'top';
const getPlacement = (dropUp: boolean, align: 'center' | 'left' | 'right'): DropdownPlacement => {
    if (dropUp) {
        if (align !== 'center') {
            return align === 'left' ? 'top-start' : 'top-end';
        }
        return 'top';
    } else {
        if (align !== 'center') {
            return align === 'left' ? 'bottom-start' : 'bottom-end';
        }
        return 'bottom';
    }
};

export const DSDropdown: React.FC<DSDropdownProps> = ({
    align = 'left',
    children,
    dropUp = false,
    render,
    renderPaper,
}) => {
    const [isOpen, toggle] = useToggle();
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const popperPlacement = getPlacement(dropUp, align);

    const dropdownPaperContent = React.useMemo(
        () => (
            <ClickAwayListener onClickAway={() => toggle('toggleOff')}>
                <div>{children(() => toggle('toggleOff'))}</div>
            </ClickAwayListener>
        ),
        [children, toggle],
    );

    return (
        <div ref={anchorRef}>
            {render(toggle)}
            <DSPopper anchorEl={anchorRef.current} open={isOpen} placement={popperPlacement} transition>
                {({ placement, TransitionProps }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: placement.includes('bottom') ? 'center bottom' : 'center top',
                        }}
                    >
                        {renderPaper ? renderPaper(dropdownPaperContent) : <DSPaper>{dropdownPaperContent}</DSPaper>}
                    </Grow>
                )}
            </DSPopper>
        </div>
    );
};
DSDropdown.displayName = 'DSDropdown';
